import { Skeleton } from '@ordo/ui/skeleton'
import { useMemo } from 'react'

import { useComponentDeliveryRatesQuery, useMilkSharesQuery } from '@/api/location-settings'
import { type Mealtime, MEALTIME_LABELS } from '@/lib/mealtime'
import { schoolSplitColumn } from '@/lib/school-orders/split-columns'

import { SplitGrid } from './prefill-settings-card'
import { SplitEditDialog } from './split-edit-dialog'
import { useSplitEditor } from './split-editor'

/**
 * The district defaults editor.
 *
 * Same `SplitGrid` as the campus dialog, pointed at the ORGANIZATION tier:
 * `writeScope` sends `scopeType: 'ORGANIZATION'` + the Organization id, and
 * `inheritedRates`/`inheritedShares` are empty because nothing sits above the
 * district — a district cell is either set or the platform 100%.
 *
 * ONE column, one value per meal part. OVS is set by the district with
 * school-level overrides (Sheila, 2026-11-04), and age-group exceptions are a
 * school concern, so offering a column axis here would author district rows no
 * surface reads back.
 *
 * Shared by the My School pre-fill card's district view and the
 * "Component split by campus" page.
 */
export function DistrictDefaultsDialog({
  open,
  onClose,
  organizationId,
  organizationName,
  anchorLocationId,
  mealtime,
}: {
  open: boolean
  onClose: () => void
  organizationId: string
  organizationName: string
  anchorLocationId: string
  mealtime: Mealtime
}) {
  const ratesQuery = useComponentDeliveryRatesQuery(anchorLocationId, 'ORGANIZATION')
  const milkSharesQuery = useMilkSharesQuery(anchorLocationId, 'ORGANIZATION')
  const editor = useSplitEditor(anchorLocationId)

  const columns = useMemo(() => [schoolSplitColumn('Every school')], [])

  const pending = ratesQuery.isPending || milkSharesQuery.isPending
  return (
    <SplitEditDialog
      open={open}
      onClose={onClose}
      editor={editor}
      title={`District defaults — ${MEALTIME_LABELS[mealtime]}`}
      description={
        <>
          What every school in {organizationName} ships at {MEALTIME_LABELS[mealtime].toLowerCase()} unless it sets its
          own number. A school that overrides a value keeps its own. Nothing is written until you press{' '}
          <span className="font-medium text-foreground">Save changes</span>, and saving applies to orders placed from
          now on.
        </>
      }>
      {pending ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <SplitGrid
          locationId={anchorLocationId}
          columns={columns}
          mealtime={mealtime}
          rates={ratesQuery.data?.rates ?? []}
          shares={milkSharesQuery.data?.shares ?? []}
          inheritedRates={[]}
          inheritedShares={[]}
          milkSkus={milkSharesQuery.data?.milkSkus ?? []}
          milkProfile={milkSharesQuery.data?.milkProfile ?? { milkOptionCodes: [], noMilkMode: false }}
          editor={editor}
          writeScope={{ scopeType: 'ORGANIZATION', scopeId: organizationId }}
        />
      )}
    </SplitEditDialog>
  )
}
